// src/services/datev/DatevPayrollReportsService.ts

import { netlifyFetch } from '../../shared/services/netlifyFetch';

export interface DatevClient {
  client_id: string;
  consultant_number: number;
  client_number: number;
}

export interface DatevDocumentType {
  document_type: string;
  access: 'access_granted' | 'access_denied';
}

export interface DatevDocumentMetadataEntry {
  period: string;
  timestamp: string;
}

export interface DatevEmployeeDocument {
  document_type: string;
  employees: {
    employee_number: number;
    document: DatevDocumentMetadataEntry[];
  }[];
}

export interface DatevClientDocument {
  document_type: string;
  document: DatevDocumentMetadataEntry[];
}

export interface DatevDocumentsMetadata {
  employee_documents: DatevEmployeeDocument[];
  client_documents: DatevClientDocument[];
}

export interface DatevApiError {
  error: string;
  datev_error?: string;
  help_url?: string;
  requires_reconnect?: boolean;
}

// Alle verfügbaren Dokumententypen
export const DOCUMENT_TYPES: Record<string, string> = {
  LOBN: 'Brutto/Netto',
  LSTB: 'Lohnsteuerbescheinigung (DE)',
  LSTE: 'Lohnsteuerbescheinigung (EN)',
  SVNW: 'SV-Nachweis',
  ABEG: 'A1-Bescheinigung',
  AANB: 'Antragsbestätigung A1',
  BUBE: 'Buchungsbeleg',
  LOJO: 'Lohnjournal (DE)',
  LJOE: 'Lohnjournal (EN)',
  LSTA: 'Lohnsteueranmeldung',
  KBNW: 'Beitragsnachweis',
  SBNW: 'Beitragsnachweis-Erläuterung',
  ZAKF: 'Übersicht Zahlungen',
  PRZA: 'DÜ Zahlungen',
  ZABR: 'Barauszahlungswerte',
  ZAUW: 'Überzahlungen-Werte',
  LOPS: 'Personalkostenübersicht (DE)',
  LOPE: 'Personalkostenübersicht (EN)',
  KOST: 'Kostenstellenwerte',
  KOTR: 'Kostenträgerwerte',
  LOWE: 'Lohnartenwerte einfach',
  LOPN: 'Lohnartenwerte erweitert',
  LKTO: 'Lohnkonto',
  LORE: 'Personalreport',
  PDAT: 'Mitarbeiterstammdaten',
  PFAN: 'Pfändungswerte',
  DAWE: 'Darlehenswerte',
  WEAN: 'Wertguthaben je Arbeitnehmer'
};

class DatevPayrollReportsService {
  private getApiBaseUrl(): string {
    return 'https://intranet.xtax.de/.netlify/functions/datev-payrollreports';
  }

  /**
   * Liste aller freigegebenen Bestände (MUST: Berechtigungsprüfung)
   */
  async getClients(customerId: string): Promise<DatevClient[]> {
    const response = await netlifyFetch(
      `${this.getApiBaseUrl()}/clients?customerId=${customerId}`
    );

    if (!response.ok) {
      const error: DatevApiError = await response.json();
      throw error;
    }

    return response.json();
  }

  /**
   * Dokumententypen mit Zugriffsrechten (access_granted / access_denied)
   * DATEV Response-Struktur: { client_id, consultant_number, client_number, 
   *   document_types: { access_granted: ["LOBN", ...], access_denied: ["AANB", ...] } }
   */
  async getDocumentTypes(customerId: string, clientId: string): Promise<DatevDocumentType[]> {
    const response = await netlifyFetch(
      `${this.getApiBaseUrl()}/clients/${clientId}?customerId=${customerId}`
    );

    if (!response.ok) {
      const error: DatevApiError = await response.json();
      throw error;
    }

    const data = await response.json();
    const docTypes = data.document_types || data;
    const result: DatevDocumentType[] = [];

    // DATEV-Format: { access_granted: ["LOBN", ...], access_denied: ["AANB", ...] }
    if (docTypes.access_granted && Array.isArray(docTypes.access_granted)) {
      for (const type of docTypes.access_granted) {
        result.push({ document_type: type, access: 'access_granted' });
      }
      if (docTypes.access_denied && Array.isArray(docTypes.access_denied)) {
        for (const type of docTypes.access_denied) {
          result.push({ document_type: type, access: 'access_denied' });
        }
      }
      return result;
    }

    // Fallback: bereits im Zielformat (Array von DatevDocumentType)
    if (Array.isArray(docTypes)) {
      return docTypes;
    }

    // Fallback: Objekt mit { "LOBN": "access_granted", ... }
    return Object.entries(docTypes).map(([key, value]) => ({
      document_type: key,
      access: (typeof value === 'string' ? value : 'access_denied') as 'access_granted' | 'access_denied'
    }));
  }

  /**
   * Verfügbare Auswertungen (Metadata)
   */
  async getDocumentsMetadata(
    customerId: string,
    clientId: string,
    documentTypes: string[],
    options?: { period?: string; timestamp?: string }
  ): Promise<DatevDocumentsMetadata> {
    const params = new URLSearchParams({
      customerId,
      document_types: documentTypes.join(',')
    });
    if (options?.period) params.append('period', options.period);
    if (options?.timestamp) params.append('timestamp', options.timestamp);

    const response = await netlifyFetch(
      `${this.getApiBaseUrl()}/clients/${clientId}/documents-metadata?${params}`
    );

    if (!response.ok) {
      const error: DatevApiError = await response.json();
      throw error;
    }

    return response.json();
  }

  /**
   * Prüfung ob Dokumente für Zeitraum existieren
   */
  async checkDocumentsExist(
    customerId: string,
    clientId: string,
    period: string
  ): Promise<boolean> {
    const response = await netlifyFetch(
      `${this.getApiBaseUrl()}/clients/${clientId}/documents/${period}/status?customerId=${customerId}`
    );

    if (!response.ok) {
      const error: DatevApiError = await response.json();
      throw error;
    }

    const result = await response.json();
    return result === true || result === 'true';
  }

  /**
   * Personalnummern für einen Zeitraum
   */
  async getEmployeeNumbers(
    customerId: string,
    clientId: string,
    period: string,
    documentTypes: string[]
  ): Promise<number[]> {
    const params = new URLSearchParams({
      customerId,
      document_types: documentTypes.join(',')
    });

    const response = await netlifyFetch(
      `${this.getApiBaseUrl()}/clients/${clientId}/documents/${period}/employee_numbers?${params}`
    );

    if (!response.ok) {
      const error: DatevApiError = await response.json();
      throw error;
    }

    return response.json();
  }

  /**
   * Dokumente herunterladen (PDF oder ZIP)
   * Gibt einen Blob zurück, der als Download angeboten werden kann
   */
  async downloadDocuments(
    customerId: string,
    clientId: string,
    period: string,
    documentTypes: string[],
    options?: { employeeNumber?: number; asZip?: boolean }
  ): Promise<{ blob: Blob; filename: string }> {
    const params = new URLSearchParams({
      customerId,
      document_types: documentTypes.join(',')
    });
    if (options?.employeeNumber) {
      params.append('employee_number', options.employeeNumber.toString());
    }

    const accept = options?.asZip ? 'application/zip' : 'application/pdf';

    const response = await netlifyFetch(
      `${this.getApiBaseUrl()}/clients/${clientId}/documents/${period}?${params}`,
      {
        headers: { 'Accept': accept }
      }
    );

    if (!response.ok) {
      // Bei Fehler ist die Response JSON (nicht base64)
      const error: DatevApiError = await response.json();
      throw error;
    }

    // Netlify gibt base64-encodierte Binärdaten zurück
    const contentType = response.headers.get('content-type') || '';

    let blob: Blob;
    if (contentType.includes('application/json')) {
      // Fallback: wenn die Response als JSON kommt (sollte nicht passieren)
      const text = await response.text();
      const bytes = Uint8Array.from(atob(text), c => c.charCodeAt(0));
      blob = new Blob([bytes], { type: accept });
    } else {
      blob = await response.blob();
    }

    // Dateiname generieren
    const disposition = response.headers.get('content-disposition') || '';
    let filename = `DATEV_${documentTypes.join('-')}_${period}`;
    if (options?.employeeNumber) filename += `_MA${options.employeeNumber}`;
    filename += options?.asZip ? '.zip' : '.pdf';

    // Versuche Dateiname aus Content-Disposition zu extrahieren
    const filenameMatch = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
    if (filenameMatch) {
      filename = filenameMatch[1].replace(/['"]/g, '');
    }

    return { blob, filename };
  }

  /**
   * Hilfsmethode: Download im Browser triggern
   */
  triggerDownload(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }
}

export const datevPayrollReportsService = new DatevPayrollReportsService();
